import { FunctionComponent, useEffect, useLayoutEffect, useMemo, useState } from "react";

import { createPortal } from "react-dom";

import {
	faBolt,
	faCircleCheck,
	faClipboardCheck,
	faPenLine,
	faSparkles,
	faUserSlash,
} from "@fortawesome/pro-regular-svg-icons";
import { faLock, faPlus } from "@fortawesome/pro-solid-svg-icons";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Icon } from "@/components";

import { useAppSelector } from "@/redux/hooks";

import { useGetListOutreachConfigQuery } from "@/pages/Private/redux/list/list-outreach.api";
import { listOutreachConfigSelector } from "@/pages/Private/redux/list/list-outreach.slice";

import { classNames } from "@/utils/classNames";

import { listHasValidCampaign } from "../../utils/columns/reachOutStatus";

import { ReachOutAiPanel } from "./ReachOutAiPanel";
import { ReachOutBulkActionItem } from "./ReachOutBulkActionItem";
import { listHasValidCampaignUsingAiSnippets } from "./reachOutCampaigns";
import { reachOutCampaignsEnabled } from "./reachOutCampaignsEnabled";
import { ReachOutCampaignPicker } from "./ReachOutCampaignPicker";
import {
	ReachOutTarget,
	reachOutTargetCount,
	reachOutTargetList,
	reachOutTargetListId,
} from "./reachOutTarget";

type Expanded = "none" | "campaign" | "review" | "ai";

interface ReachOutMenuProps {
	/** What the menu acts on — a single lead (Outreach column) or a bulk selection (footer). */
	target: ReachOutTarget;
	/** Trigger rect (viewport coords) the menu anchors to. */
	anchorRect: DOMRect | null;
	onClose: () => void;
	/** "Write manually" → open the lead's chat composer (single lead only; disabled in bulk). */
	onManual: () => void;
	/**
	 * Bulk-only: open the "Run AI Snippets" confirm modal for the selection. When set, a "Run AI
	 * Snippets" item is offered — but only if a valid campaign in the list actually uses AI snippets.
	 */
	onRunAiSnippets?: () => void;
	/** Bulk-only: open the "Revert Assigned Campaign" confirm modal for the selection (assigned leads). */
	onRevertAssignedCampaign?: () => void;
	/** Bulk-only: open the "Confirm preassigned campaigns" dialog for the selection (PREC-4219). */
	onConfirmPreassigned?: () => void;
	/** Seed text prefilled into "Let AI write a sequence" (feed run summary, PREC-3635 P6). */
	aiTriggerDescription?: string | null;
	/**
	 * Tracker signal that "Let AI write a sequence" defaults to (the card's main signal, PREC-3710) —
	 * shown as a removable chip and used as the outreach trigger. Removing the chip writes without it.
	 */
	aiSeedSignalId?: number;
	/**
	 * Hide the two campaign paths ("Add to campaign" / "Review & edit campaign") and their divider.
	 * The Feed reaches out per-signal, where the list-campaign flows don't apply, so it opts out and
	 * shows only "Let AI write a sequence" + "Write manually".
	 */
	hideCampaignActions?: boolean;
	portalContainer?: HTMLElement | null;
}

// The menu is compact by default (MENU_WIDTH_COMPACT, matching the mockup). It grows to MENU_WIDTH
// only for the two width-hungry panels — the embedded GenerateBar control row (language + sender +
// channel + Generate) under "Let AI write a sequence" and the reused campaign-editor senders panel
// under "Change the senders of the campaign" — which need the extra room to lay out without cramping.
const MENU_WIDTH = 640;
const MENU_WIDTH_COMPACT = 392;
const MARGIN = 8;

/** Colored 32px icon tile at the head of each menu item. */
const IconTile: FunctionComponent<{ icon: typeof faBolt; className: string }> = ({
	icon,
	className,
}) => (
	<div
		className={classNames(
			"flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
			className
		)}
	>
		<Icon className="h-4 w-4" icon={icon} />
	</div>
);

/**
 * The per-lead "Reach out" menu — a body-portaled dropdown (web rule §11) offering the four outreach
 * paths from the mockup: Add to campaign (inline picker), Review & edit campaign, Let AI write a
 * sequence (inline GenerateBar), Write manually. The two campaign paths lock with a "Create a
 * campaign" prompt when the list has no valid campaign; opening one expandable path dims the others.
 */
export const ReachOutMenu: FunctionComponent<ReachOutMenuProps> = ({
	target,
	anchorRect,
	onClose,
	onManual,
	onRunAiSnippets,
	onRevertAssignedCampaign,
	onConfirmPreassigned,
	aiTriggerDescription,
	aiSeedSignalId,
	hideCampaignActions = false,
	portalContainer,
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [expanded, setExpanded] = useState<Expanded>("none");
	const [menuEl, setMenuEl] = useState<HTMLDivElement | null>(null);
	const [pos, setPos] = useState<{ top: number; left: number }>({ top: -9999, left: -9999 });
	// The campaign picker owns its "Change senders" toggle and reports its state up here so the menu
	// can widen while that panel is open (reset in `toggle` when the section changes).
	const [sendersPanelOpen, setSendersPanelOpen] = useState(false);

	const isBulk = target.kind === "bulk";
	const listId = reachOutTargetListId(target);
	const count = reachOutTargetCount(target);
	const hasCampaign = listHasValidCampaign(reachOutTargetList(target));

	// A list can switch campaigns off entirely (PREC-3687 D5). The menu is this surface's only
	// consumer of the config, so it triggers the fetch and reads the value from the slice (web rule
	// §2); until it lands, the list's source-type default decides.
	useGetListOutreachConfigQuery(listId, { skip: !listId });
	const outreachConfig = useAppSelector(listOutreachConfigSelector(listId));
	// The Feed opts out of the campaign paths per-signal (`hideCampaignActions`); campaigns being
	// switched off hides the same two entries. Both reasons compose — either one suppresses them.
	const showCampaignPaths =
		!hideCampaignActions && reachOutCampaignsEnabled(reachOutTargetList(target), outreachConfig);
	// "Run AI Snippets" is only meaningful when a valid campaign actually resolves an AI_SNIPPET column.
	const showRunAiSnippets =
		!!onRunAiSnippets && listHasValidCampaignUsingAiSnippets(reachOutTargetList(target));
	const firstName =
		target.kind === "lead"
			? (target.row.prospect?.firstName ?? target.row.prospect?.name ?? "")
			: "";
	const heading = isBulk
		? t("list.reachOut.menuHeadingBulk", { count })
		: t("list.reachOut.menuHeading", { name: firstName });
	const reviewDescription = isBulk
		? t("list.reachOut.reviewCampaign.descriptionBulk")
		: t("list.reachOut.reviewCampaign.description", { name: firstName });

	// Wide for the two panels that need the room ("Let AI write a sequence" + the senders panel);
	// compact otherwise (see the width constants above).
	const menuWidth = expanded === "ai" || sendersPanelOpen ? MENU_WIDTH : MENU_WIDTH_COMPACT;

	// Position the menu under the trigger, flipping above / clamping so it never leaves the viewport.
	// Re-runs when a panel expands (height changes) so the grown menu stays on screen.
	useLayoutEffect(() => {
		if (!anchorRect || !menuEl) {
			return;
		}

		const height = menuEl.offsetHeight;
		let top = anchorRect.bottom + MARGIN;

		// If opening below would clip the viewport bottom (e.g. the footer "Reach out" button or a lead
		// near the end of the list), flip above the trigger — bottom-aligned to its top so the menu
		// stays anchored to the button and grows upward as inline panels expand.
		if (top + height > window.innerHeight - MARGIN) {
			top = anchorRect.top - height - MARGIN;
		}

		// Never run off the top edge.
		top = Math.max(MARGIN, top);

		const left = Math.max(
			MARGIN,
			Math.min(anchorRect.left, window.innerWidth - menuWidth - MARGIN)
		);

		setPos({ top, left });
	}, [anchorRect, menuEl, expanded, menuWidth]);

	// Close on Escape. Outside clicks are handled by the backdrop below (not a global mousedown
	// listener) so that GenerateBar's body-portaled dropdowns — which sit *above* this menu with
	// their own backdrop — don't count as "outside" and dismiss it.
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				onClose();
			}
		};

		document.addEventListener("keydown", onKey);

		return () => document.removeEventListener("keydown", onKey);
	}, [onClose]);

	const toggle = (next: Expanded) => {
		// Reset the widen flag on every section change — switching paths unmounts the picker, so its
		// last reported "Change senders" state must never leave the menu stuck wide.
		setSendersPanelOpen(false);
		setExpanded((prev) => (prev === next ? "none" : next));
	};

	const dimmed = (self: Expanded) => expanded !== "none" && expanded !== self;

	const createCampaign = () => {
		navigate(`/app/lists/create/copywriting/${listId}?createCampaign=true`);
		onClose();
	};

	const lockedPopover = useMemo(
		() => (message: string) => (
			<div className="invisible absolute left-2 right-2 top-[calc(100%-6px)] z-10 rounded-lg border border-border-light bg-white p-3 opacity-0 shadow-lg transition-opacity group-hover/item:visible group-hover/item:opacity-100">
				<p className="mb-2 text-[11.5px] leading-snug text-text-meta">{message}</p>
				<button
					className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-lg bg-blue-action px-3 text-xs font-semibold text-white hover:bg-blue-action-dark"
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						createCampaign();
					}}
				>
					<Icon className="h-3 w-3" icon={faPlus} />
					{t("list.reachOut.createCampaign")}
				</button>
			</div>
		),
		[t]
	);

	const menu = (
		<div
			ref={setMenuEl}
			className="fixed z-[1200] flex max-h-[calc(100vh-16px)] flex-col overflow-y-auto rounded-xl border border-border-light bg-white p-1.5 shadow-lg"
			data-testid="reach-out-menu"
			style={{ top: pos.top, left: pos.left, width: menuWidth }}
			onClick={(e) => e.stopPropagation()}
		>
			<div className="px-2.5 pb-1.5 pt-2 text-[11px] font-semibold uppercase tracking-wide text-text-disabled">
				{heading}
			</div>

			{showCampaignPaths && (
				<>
					{/* Add to campaign — inline picker, or locked with a create-campaign prompt. The
					    `data-coachmark` hook anchors the assign-leads onboarding flow (AssignLeadsCoachmark). */}
					<div
						className={classNames(
							"group/item relative flex cursor-pointer gap-3 rounded-lg p-2.5 transition-opacity hover:bg-gray-100",
							!hasCampaign ? "cursor-default hover:bg-transparent" : "",
							dimmed("campaign") ? "opacity-40" : ""
						)}
						data-coachmark="add-to-campaign"
						onClick={() => hasCampaign && toggle("campaign")}
					>
						<IconTile className="bg-blue-action-200 text-blue-action" icon={faSparkles} />
						<div className="min-w-0 flex-1">
							<div className="flex items-center gap-1.5 text-[13.5px] font-semibold text-brand-black">
								{t("list.reachOut.addToCampaign.title")}
								{!hasCampaign && <Icon className="h-3 w-3 text-text-disabled" icon={faLock} />}
							</div>
							<div className="mt-0.5 text-[11.5px] leading-snug text-text-meta">
								{hasCampaign
									? t("list.reachOut.addToCampaign.description")
									: t("list.reachOut.addToCampaign.lockedDescription")}
							</div>
						</div>
						{!hasCampaign && lockedPopover(t("list.reachOut.addToCampaign.lockedPopover"))}
					</div>

					{hasCampaign && expanded === "campaign" && (
						<ReachOutCampaignPicker
							target={target}
							onDone={onClose}
							onSendersPanelChange={setSendersPanelOpen}
						/>
					)}

					{/* Review & edit campaign — same inline picker as "Add to campaign", but its terminal action
					    pre-assigns the lead (campaignId + addedToCampaignAt, no send) and jumps to ReadyToSend. */}
					<div
						className={classNames(
							"group/item relative flex cursor-pointer gap-3 rounded-lg p-2.5 transition-opacity hover:bg-gray-100",
							!hasCampaign ? "cursor-default hover:bg-transparent" : "",
							dimmed("review") ? "opacity-40" : ""
						)}
						onClick={() => hasCampaign && toggle("review")}
					>
						<IconTile
							className="bg-status-success-light text-status-success"
							icon={faClipboardCheck}
						/>
						<div className="min-w-0 flex-1">
							<div className="flex items-center gap-1.5 text-[13.5px] font-semibold text-brand-black">
								{t("list.reachOut.reviewCampaign.title")}
								{!hasCampaign && <Icon className="h-3 w-3 text-text-disabled" icon={faLock} />}
							</div>
							<div className="mt-0.5 text-[11.5px] leading-snug text-text-meta">
								{reviewDescription}
							</div>
						</div>
						{!hasCampaign && lockedPopover(t("list.reachOut.reviewCampaign.lockedPopover"))}
					</div>

					{hasCampaign && expanded === "review" && (
						<ReachOutCampaignPicker
							target={target}
							variant="assign"
							onDone={onClose}
							onSendersPanelChange={setSendersPanelOpen}
						/>
					)}
				</>
			)}

			{/* Run AI Snippets — bulk-only; offered only when a valid campaign uses AI snippets. Opens the
			    owner's confirm modal (setShowRunAiSnippetsModal) over the selection. */}
			{showRunAiSnippets && (
				<div
					className={classNames(
						"group/item flex cursor-pointer gap-3 rounded-lg p-2.5 transition-opacity hover:bg-gray-100",
						dimmed("none") ? "opacity-40" : ""
					)}
					data-testid="reach-out-run-ai-snippets"
					onClick={() => {
						onRunAiSnippets?.();
						onClose();
					}}
				>
					<IconTile className="bg-blue-action-200 text-blue-action" icon={faSparkles} />
					<div className="min-w-0 flex-1">
						<div className="text-[13.5px] font-semibold text-brand-black">
							{t("list.reachOut.runAiSnippets.title")}
						</div>
						<div className="mt-0.5 text-[11.5px] leading-snug text-text-meta">
							{t("list.reachOut.runAiSnippets.description")}
						</div>
					</div>
				</div>
			)}

			{/* Divider between the campaign/snippet section above and the AI/manual section below —
			    dropped when nothing sits above it (Feed hides the campaign paths). */}
			{(showCampaignPaths || showRunAiSnippets) && (
				<div className="mx-2 my-1 h-px bg-border-light" />
			)}

			{/* Let AI write a sequence — inline GenerateBar */}
			<div
				className={classNames(
					"group/item flex cursor-pointer gap-3 rounded-lg p-2.5 transition-opacity hover:bg-gray-100",
					dimmed("ai") ? "opacity-40" : ""
				)}
				onClick={() => toggle("ai")}
			>
				<IconTile className="bg-blue-action-200 text-blue-action" icon={faBolt} />
				<div className="min-w-0 flex-1">
					<div className="text-[13.5px] font-semibold text-brand-black">
						{t("list.reachOut.letAiWrite.title")}
					</div>
					<div className="mt-0.5 text-[11.5px] leading-snug text-text-meta">
						{t("list.reachOut.letAiWrite.description")}
					</div>
				</div>
			</div>

			{expanded === "ai" && (
				<ReachOutAiPanel
					portalContainer={portalContainer}
					seedSignalId={aiSeedSignalId}
					target={target}
					triggerDescription={aiTriggerDescription}
					onGenerated={onClose}
				/>
			)}

			{/* Write manually — single lead only; bulk composes per lead, so it is locked here. */}
			<div
				className={classNames(
					"group/item relative flex gap-3 rounded-lg p-2.5 transition-opacity",
					isBulk
						? "cursor-default opacity-60"
						: classNames("cursor-pointer hover:bg-gray-100", dimmed("none") ? "opacity-40" : "")
				)}
				onClick={() => {
					if (isBulk) {
						return;
					}

					onManual();
					onClose();
				}}
			>
				<IconTile className="bg-status-info-light text-text-meta" icon={faPenLine} />
				<div className="min-w-0 flex-1">
					<div className="flex items-center gap-1.5 text-[13.5px] font-semibold text-brand-black">
						{t("list.reachOut.writeManually.title")}
						{isBulk && <Icon className="h-3 w-3 text-text-disabled" icon={faLock} />}
					</div>
					<div className="mt-0.5 text-[11.5px] leading-snug text-text-meta">
						{isBulk
							? t("list.reachOut.writeManually.lockedDescriptionBulk")
							: t("list.reachOut.writeManually.description")}
					</div>
				</div>
			</div>

			{/* Revert Assigned Campaign — bulk-only; shown when the selection has assigned leads. Opens the
			    owner's confirm modal (setShowRevertCampaignAssignmentModal). */}
			{onRevertAssignedCampaign && (
				<ReachOutBulkActionItem
					description={t("list.reachOut.revertCampaign.description")}
					dimmed={dimmed("none")}
					icon={faUserSlash}
					iconClassName="bg-status-error-light text-status-error"
					testId="reach-out-revert-campaign"
					title={t("list.reachOut.revertCampaign.title")}
					onClick={() => {
						onRevertAssignedCampaign();
						onClose();
					}}
				/>
			)}
			{/* Confirm preassigned campaigns (PREC-4219) — bulk-only: each selected lead goes into the
			    campaign it already holds; no campaign is picked here. Opens the owner's confirm dialog.
			    It enrolls leads into campaigns, so it is a campaign path: hidden with the others when
			    the list switches campaigns off or the caller hides them. */}
			{showCampaignPaths && onConfirmPreassigned && (
				<ReachOutBulkActionItem
					description={t("list.outreach.routing.confirm.actionDescription")}
					dimmed={dimmed("none")}
					icon={faCircleCheck}
					iconClassName="bg-status-success-light text-status-success"
					testId="reach-out-confirm-preassigned"
					title={t("list.outreach.routing.confirm.action")}
					onClick={() => {
						onConfirmPreassigned();
						onClose();
					}}
				/>
			)}
		</div>
	);

	return createPortal(
		<>
			{/* Click-catcher: closes on any outside click. Sits just below the menu, so GenerateBar's
			    body-portaled dropdowns/modals (higher z, own backdrop) stay clickable without dismissing. */}
			<div className="fixed inset-0 z-[1190]" onMouseDown={onClose} />
			{menu}
		</>,
		portalContainer ?? document.body
	);
};
